import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
  PageHeader,
  Table,
  Input,
  Avatar,
  Tag,
  Layout,
  Tabs,
  Dropdown,
  Menu,
  Button,
  Modal,
  message,
  Typography
} from 'antd';
import {
  SearchOutlined,
  MoreOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { getMembers, toggleMemberActive, bulkActivateMembers } from './workspaceMembers.action';

const { confirm } = Modal;

const { Content } = Layout;
const { TabPane } = Tabs;
const { Text } = Typography;
class WorkspaceMembers extends Component {
  constructor(props) {
    super();
    this.state = {
      activeSearch: '',
      inactiveSearch: '',
      activeSearchTimeout: null,
      inactiveSearchTimeout: null,
      activeTab: 'active',
      selectedInactiveKeys: [],
      bulkLoading: false,
    };
  }

  componentDidMount() {
    this.fetchActive();
    this.fetchInactive();
  }

  fetchActive = (page = 1, search = this.state.activeSearch) => {
    const { wId } = this.props.match.params;
    this.props.getMembers(wId, { page, limit: 10, search, is_active: true });
  };

  fetchInactive = (page = 1, search = this.state.inactiveSearch) => {
    const { wId } = this.props.match.params;
    this.props.getMembers(wId, { page, limit: 10, search, is_active: false });
  };

  onSearchChange = (tab) => (e) => {
    const search = e.target.value;
    if (tab === 'active') {
      clearTimeout(this.state.activeSearchTimeout);
      const activeSearchTimeout = setTimeout(() => this.fetchActive(1, search), 400);
      this.setState({ activeSearch: search, activeSearchTimeout });
    } else {
      clearTimeout(this.state.inactiveSearchTimeout);
      const inactiveSearchTimeout = setTimeout(() => this.fetchInactive(1, search), 400);
      this.setState({ inactiveSearch: search, inactiveSearchTimeout });
    }
  };

  handleToggle = async (wmId, makeActive) => {
    const { wId } = this.props.match.params;
    try {
      const res = await this.props.toggleMemberActive(wId, wmId, makeActive);
      const data = res && res.data;
      if (data && data.errors === 'change_plan') {
        message.warning(
          `Plan limit reached. You can activate ${data.usersLimitAvailable ?? 0} more member${data.usersLimitAvailable !== 1 ? 's' : ''} on your current plan. Upgrade to activate more.`
        );
        return;
      }
      message.success(makeActive ? 'Member activated' : 'Member deactivated');
      this.fetchActive();
      this.fetchInactive();
    } catch {
      message.error('Something went wrong');
    }
  };

  handleBulkActivate = async () => {
    const { wId } = this.props.match.params;
    const { selectedInactiveKeys } = this.state;
    this.setState({ bulkLoading: true });
    try {
      const data = await this.props.bulkActivateMembers(wId, selectedInactiveKeys);
      if (data.success) {
        message.success(`${data.activated} member${data.activated !== 1 ? 's' : ''} activated`);
        this.setState({ selectedInactiveKeys: [] });
        this.fetchActive();
        this.fetchInactive();
      } else if (data.errors === 'change_plan') {
        message.warning(
          `Plan limit reached. You can activate ${data.usersLimitAvailable ?? 0} more member${data.usersLimitAvailable !== 1 ? 's' : ''} on your current plan. Upgrade to activate more.`
        );
      } else {
        message.error('Failed to activate members');
      }
    } catch {
      message.error('Something went wrong');
    } finally {
      this.setState({ bulkLoading: false });
    }
  };

  getMemberName = (record) =>
    (record.slackInfo && record.slackInfo.displayName) ||
    (record.user_id && (record.user_id.displayName || record.user_id.name)) ||
    record.email ||
    '';

  confirmDeactivate = (record) => {
    const { user, isAdmin } = this.props;

    if (!isAdmin) {
      message.error('Only admins can deactivate members.');
      return;
    }

    const recordUserId = record.user_id && (record.user_id._id || record.user_id);
    const currentUserId = user && (user._id || user.id);
    if (recordUserId && currentUserId && String(recordUserId) === String(currentUserId)) {
      message.error('You cannot deactivate yourself.');
      return;
    }

    const name = this.getMemberName(record);

    if (record.role === 'admin') {
      confirm({
        title: `${name} is an admin`,
        icon: <ExclamationCircleOutlined />,
        content: 'Please remove them from admin role before deactivating.',
        okText: 'OK',
        cancelButtonProps: { style: { display: 'none' } },
      });
      return;
    }

    confirm({
      title: `Deactivate ${name}?`,
      icon: <ExclamationCircleOutlined />,
      content: 'This member will lose access to the workspace. You can reactivate them any time.',
      okText: 'Deactivate',
      // okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: () => this.handleToggle(record._id, false),
    });
  };

  confirmActivate = (record) => {
    const name = this.getMemberName(record);
    confirm({
      title: `Activate ${name}?`,
      icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
      content: 'This member will regain access to the workspace.',
      okText: 'Activate',
      cancelText: 'Cancel',
      onOk: () => this.handleToggle(record._id, true),
    });
  };

  confirmBulkActivate = () => {
    const { selectedInactiveKeys } = this.state;
    const count = selectedInactiveKeys.length;
    confirm({
      title: `Activate ${count} member${count > 1 ? 's' : ''}?`,
      icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
      content: `${count} member${count > 1 ? 's' : ''} will regain access to the workspace.`,
      okText: 'Activate',
      cancelText: 'Cancel',
      onOk: this.handleBulkActivate,
    });
  };

  getMemberColumns = (isActive) => [
    {
      title: 'Member',
      key: 'member',
      render: (_, record) => {
        const displayName = this.getMemberName(record);
        const profilePic = record.slackInfo && record.slackInfo.profilePicUrl;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Avatar src={profilePic}>
              {displayName ? displayName[0].toUpperCase() : '?'}
            </Avatar>
            <span>{displayName}</span>
          </div>
        );
      },
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role) => (
        <Tag color={role === 'admin' ? 'purple' : 'default'}>
          {role ? role.charAt(0).toUpperCase() + role.slice(1) : 'Member'}
        </Tag>
      ),
    },
    {
      title: '',
      key: 'action',
      // width: 48,
      render: (_, record) =>
        isActive ? (
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item key='deactivate' danger onClick={() => this.confirmDeactivate(record)}>
                  Deactivate
                </Menu.Item>
              </Menu>
            }
            trigger={['click']}
          >
            <MoreOutlined style={{ fontSize: 18, cursor: 'pointer' }} />
          </Dropdown>
        ) : (
          <Button type='link' onClick={(e) => { e.preventDefault(); this.confirmActivate(record); }}>
            Activate
          </Button>
        ),
    },
  ];

  render() {
    const {
      activeMembers,
      inactiveMembers,
      activePagination,
      inactivePagination,
      loading,
    } = this.props;
    const { activeSearch, inactiveSearch, activeTab, selectedInactiveKeys, bulkLoading } = this.state;
    const activeTotal = activePagination && activePagination.total;
    const inactiveTotal = inactivePagination && inactivePagination.total;

    return (
      <div className='flex-col' style={{ width: '100%', overflow: 'hidden' }}>
        <PageHeader title='Members' />

        <Tabs style={{ paddingLeft: '32px', width: '100%', boxSizing: 'border-box' }}
          activeKey={activeTab}

          onChange={(key) => this.setState({ activeTab: key })}
        >
          <TabPane tab={`Active${activeTotal ? ` (${activeTotal})` : ''}`} key='active' style={{ overflow: 'auto', width: '100%', paddingRight: '32px', paddingBottom: '32px' }}>
            <Input
              prefix={<SearchOutlined />}
              placeholder='Search active members'
              value={activeSearch}
              onChange={this.onSearchChange('active')}
              style={{ marginBottom: 16, maxWidth: 400 }}
              allowClear
            />
            <Table

              rowKey='_id'
              size='small'
              dataSource={activeMembers}
              columns={this.getMemberColumns(true)}
              loading={loading}
              onChange={(p) => this.fetchActive(p.current, activeSearch)}
              scroll={{ y: 'calc(100vh - 220px)' }}
              style={{ width: '100%' }}
              pagination={{
                current: activePagination && activePagination.page,
                total: activeTotal,
                pageSize: activePagination && activePagination.limit,
                showTotal: (t) => `${t} active members`,
              }}
            />
          </TabPane>

          <TabPane
            tab={`Deactivated${inactiveTotal ? ` (${inactiveTotal})` : ''}`}
            key='inactive'
            style={{ overflow: 'auto', width: '100%', paddingRight: '32px', paddingBottom: '32px' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <Input
                prefix={<SearchOutlined />}
                placeholder='Search deactivated members'
                value={inactiveSearch}
                onChange={this.onSearchChange('inactive')}
                style={{ maxWidth: 400 }}
                allowClear
              />
              {selectedInactiveKeys.length > 0 && (
                <Button type='primary' loading={bulkLoading} onClick={this.confirmBulkActivate}>
                  Activate Selected ({selectedInactiveKeys.length})
                </Button>
              )}
            </div>
            <Table
              rowKey='_id'
              dataSource={inactiveMembers}
              columns={this.getMemberColumns(false)}
              loading={loading}
              onChange={(p) => this.fetchInactive(p.current, inactiveSearch)}
              scroll={{ y: 'calc(100vh - 220px)' }}
              rowSelection={{
                selectedRowKeys: selectedInactiveKeys,
                onChange: (keys) => this.setState({ selectedInactiveKeys: keys }),
              }}
              pagination={{
                current: inactivePagination && inactivePagination.page,
                total: inactiveTotal,
                pageSize: inactivePagination && inactivePagination.limit,
                showTotal: (t) => `${t} deactivated members`,
              }}
            />
          </TabPane>
        </Tabs>

      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  activeMembers: state.workspaceMembers.activeMembers,
  inactiveMembers: state.workspaceMembers.inactiveMembers,
  activePagination: state.workspaceMembers.activePagination,
  inactivePagination: state.workspaceMembers.inactivePagination,
  loading: state.workspaceMembers.loading,
  user: state.common_reducer.user,
  isAdmin: state.common_reducer.isAdmin,
});

export default withRouter(
  connect(mapStateToProps, { getMembers, toggleMemberActive, bulkActivateMembers })(WorkspaceMembers)
);
